import { useState, type ReactNode } from 'react'

import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { getSellerCollectsPayment } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import type { SellerCollectsConfig } from '@b-stock/fe-core/bstock-next/queries'
import {
  activeSellerSubscriptionServiceQuery,
  getSellerCollectsConfig,
} from '@b-stock/fe-core/bstock-next/queries'
import { Button, FormattedMessage, Link } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import { WireTransferInstructionsModal } from '@b-stock/fe-core/pay-ui'
import { StoreFrontOptionsSellerCollectsDtoShippingArrangedByEnum as ShippingArrangedBy } from '@b-stock/listing-api-client'

import { paysOnlyByWire } from '@/buyer-portal/components/features/listing/acceptedPaymentMethods'
import { HowYoullPay } from '@/buyer-portal/components/features/listing/ListingActionColumn/Payment/HowYoullPay'
import { getSellerCollectsSellerName } from '@/buyer-portal/components/features/listing/sellerCollectsSellerName'
import { getFeeModel } from '@/buyer-portal/helpers/sellerCollectsFeeModel'
import { getListingSaleKind } from '@/buyer-portal/helpers/sellerCollectsSaleKind'
import { resolveShippingArrangedBy } from '@/buyer-portal/helpers/sellerCollectsShippingMode'
import { useAchSettlementMode } from '@/buyer-portal/helpers/useAchSettlementMode'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { useListingPaymentRestrictions } from '@/buyer-portal/helpers/useListingPaymentRestrictions'
import { useBuyerFacingAchAvailability } from '@/buyer-portal/helpers/usePaymentRestrictions'
import { isWireOnlyCurrency } from '@/buyer-portal/helpers/wireOnlyCurrency'
import {
  AvailablePaymentMethods,
  type AvailablePaymentMethodsRenderTextMap,
} from '@/components/AvailablePaymentMethods'
import { FeatureFlags } from '@/helpers/featureFlags'

const ns = 'Listing.actionColumn.payment.availableMethods'

type StorefrontWithOptions = {
  name?: string
  options?: { sellerCollects?: SellerCollectsConfig } | null
} | null

/** Only the parts of the listing this panel reads. */
type SellerCollectsListing = {
  accountId?: string
  sellerCollects?: boolean
  storefront?: StorefrontWithOptions
  seller?: {
    storefront?: StorefrontWithOptions
    account?: { displayName?: string | null }
  }
}

/**
 * Whether the seller track should say the goods are paid by wire: the buyer
 * learns how to pay from B-Stock's win email (the seller sends no invoice of
 * its own and no rep coordinates it), and wire is the only method published.
 * The first half is fe-core's own reading, so this card and the order page's
 * pay-the-seller card never disagree about which storefronts it covers.
 */
const isPaidByWireOnly = (config: SellerCollectsConfig | undefined): boolean =>
  getSellerCollectsPayment(config).howToPay === 'winEmailWire' &&
  paysOnlyByWire(config?.acceptedPaymentMethods)

const UnavailableMethod = styled.div`
  border-top: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  color: ${Colors.Semantic.Neutral.dark_grey};
  padding-top: 1rem;
`

const UnavailableMethodBody = styled.p`
  ${Typography.Body3}
  margin-bottom: 0;
`

const renderTextMap = (
  onWireLearnMore: () => void,
  isAchSettled: boolean,
  isAchBlocked: boolean
): AvailablePaymentMethodsRenderTextMap => {
  // A Standard buyer's order is not paid until the ACH transfer clears, so the
  // panel's speed-promise copy is replaced with siblings that disclose the
  // wait. Every other settlement mode keeps today's strings.
  const copyNs = isAchBlocked || isAchSettled ? `${ns}.settled` : ns
  const bold = { b: (chunks: ReactNode) => <strong>{chunks}</strong> }

  return {
    title: <FormattedMessage id={`${ns}.title`} />,
    introDisclaimer: <FormattedMessage id={`${ns}.introDisclaimer`} />,
    onlineTitle: <FormattedMessage id={`${copyNs}.onlineTitle`} />,
    onlineDesc: <FormattedMessage id={`${copyNs}.onlineDesc`} />,
    collapsedNote:
      isAchSettled && !isAchBlocked ? (
        <FormattedMessage id={`${ns}.settled.collapsedNote`} values={bold} />
      ) : undefined,
    cardTitle: <FormattedMessage id={`${ns}.card.title`} />,
    cardDesc: <FormattedMessage id={`${ns}.card.desc`} />,
    achTitle: <FormattedMessage id={`${ns}.ach.title`} />,
    achChip: <FormattedMessage id={`${ns}.ach.chip`} />,
    achDesc: <FormattedMessage id={`${ns}.ach.desc`} />,
    achNote:
      isAchSettled && !isAchBlocked ? (
        <FormattedMessage id={`${ns}.settled.achNote`} values={bold} />
      ) : undefined,
    netTermsTitle: <FormattedMessage id={`${ns}.netTerms.title`} />,
    netTermsDesc: (
      <FormattedMessage
        id={`${ns}.netTerms.desc`}
        values={{
          learnMoreLink: (
            <Link
              href="https://bstock.com/faq/buyer-faq/net-terms/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FormattedMessage id={`${ns}.netTerms.learnMore`} />
            </Link>
          ),
        }}
      />
    ),
    wireTitle: <FormattedMessage id={`${ns}.wire.title`} />,
    wireDesc: (
      <FormattedMessage
        id={`${ns}.wire.desc`}
        values={{
          linkToInstructions: (
            <Button
              type="button"
              appearance="plainTertiary"
              onClick={onWireLearnMore}
            >
              <FormattedMessage id={`${ns}.wire.learnMore`} />
            </Button>
          ),
        }}
      />
    ),
  }
}

const AchUnavailableLine = () => (
  <UnavailableMethod>
    <UnavailableMethodBody>
      <FormattedMessage id={`${ns}.achUnavailable.desc`} />
    </UnavailableMethodBody>
  </UnavailableMethod>
)

/**
 * Renders the available payment methods for a listing, derived contextually so
 * only methods valid for the listing being viewed are shown.
 *
 * Availability mirrors the checkout restriction model:
 * `useListingPaymentRestrictions` combines account-level online-payment blocks
 * with the listing's own credit-card configuration. Wire transfer has no block
 * flag in this domain, so it is always offered.
 *
 * Credit card is additionally gated behind `ENABLE_CC_SURFACING` so the method
 * is never surfaced until the credit-card experience is rolled out, matching
 * the LDP/ALP credit-card tag.
 */
export const AvailableMethods = ({
  isCreditCardEnabled,
  currency,
  listing,
  auctionOrOfferingEnded,
  buyerWon,
}: {
  isCreditCardEnabled?: boolean
  currency?: string
  listing?: SellerCollectsListing
  /** Closed without this buyer winning hides the Seller Collects panel. */
  auctionOrOfferingEnded?: boolean
  buyerWon?: boolean
}) => {
  const enableCcSurfacing = useFlag(FeatureFlags.ENABLE_CC_SURFACING)
  const enableLdpRedesign = useFlag(FeatureFlags.ENABLE_LDP_REDESIGN)
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )
  // Holds the listing rather than a boolean so narrowing carries into the
  // branches below; a derived boolean would leave `listing` optional there.
  const sellerCollectsListing =
    sellerCollectsEnabled && listing?.sellerCollects === true
      ? listing
      : undefined
  // Gated on the flag, not the listing: with the flag off the buyer must keep
  // today's methods rather than lose them with no panel to replace them.
  const isSellerCollects = sellerCollectsListing !== undefined
  const { isCreditCardBlocked, isACHBlocked, isNetTermsBlocked } =
    useListingPaymentRestrictions(isCreditCardEnabled, isSellerCollects)
  const { achAvailability } = useBuyerFacingAchAvailability()
  const isAchUnavailable = achAvailability === 'not-available-yet'
  const achSettlementMode = useAchSettlementMode()
  const { formatMessage } = useIntl()
  const [wireModalOpen, setWireModalOpen] = useState(false)

  // Skipped for non-Seller-Collects listings: the query is only needed to tell
  // a fee-bearing storefront from a fee-free one. Signed-out too, where it
  // rejects rather than answers.
  const isLoggedIn = useIsLoggedIn()
  const sellerAccountId = sellerCollectsListing?.accountId
  const {
    data: sellerSubscription,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
  } = activeSellerSubscriptionServiceQuery.useQuery(sellerAccountId ?? '', {
    enabled: !!sellerAccountId && isLoggedIn,
  })

  // Through the shared fee model, so a failed or skipped lookup reads as
  // "not known yet" rather than as a fee-free storefront, which would take
  // the whole card away.
  const feeModel = getFeeModel({
    isLoggedIn,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
    buyersPremium: sellerSubscription?.buyersPremium,
  })

  // Wire-only listings (CAD, EUR, GBP) never use ACH / Net Terms / card,
  // so this copy would be inaccurate for them. Mirrors the order-details gate.
  if (isWireOnlyCurrency(currency)) {
    return null
  }

  // The buyer pays the seller for the goods, so B-Stock's methods — and its
  // own wire details — must not appear. This returns before wireAccountInfo is
  // built, so WireTransferInstructionsModal is unreachable here.
  if (sellerCollectsListing) {
    // Nothing here is actionable once the listing closes to a buyer who did
    // not win, and the Shipping Information card beside it already goes. Only
    // the Seller Collects panel: an ordinary listing keeps its methods, which
    // preview what will be offered rather than what this buyer now owes.
    if (auctionOrOfferingEnded && !buyerWon) return null

    const sellerCollectsConfig = getSellerCollectsConfig(sellerCollectsListing)

    return (
      <HowYoullPay
        sellerName={
          getSellerCollectsSellerName(sellerCollectsListing.seller) || undefined
        }
        hasBuyerPremium={
          feeModel === undefined || feeModel === 'unknownFee'
            ? undefined
            : feeModel === 'feeBearing'
        }
        bstockBooksFreight={
          // Through the shared resolver, like the banner and the step builder:
          // one reading of the storefront's mode across the page.
          resolveShippingArrangedBy(sellerCollectsConfig) ===
          ShippingArrangedBy.BookedThroughBstock
        }
        paymentDueBusinessDays={sellerCollectsConfig?.paymentDueBusinessDays}
        saleKind={getListingSaleKind(sellerCollectsListing)}
        paidByWireOnly={isPaidByWireOnly(sellerCollectsConfig)}
      />
    )
  }

  // Same static account details PaymentMethodsContent shows pre-order via
  // this modal — there's no order yet at the listing-details stage.
  const wireAccountInfo = {
    bankName: formatMessage({ id: `${ns}.wire.accountInfo.bankName` }),
    routingNumber: formatMessage({
      id: `${ns}.wire.accountInfo.routingNumber`,
    }),
    accountNumber: formatMessage({
      id: `${ns}.wire.accountInfo.accountNumber`,
    }),
    swiftCode: formatMessage({ id: `${ns}.wire.accountInfo.swiftCode` }),
    forCreditOf: formatMessage({ id: `${ns}.wire.accountInfo.forCreditOf` }),
    suggestedPaymentAmount: formatMessage({
      id: `${ns}.wire.accountInfo.suggestedPaymentAmount`,
    }),
    additionalInformation: formatMessage(
      { id: `${ns}.wire.accountInfo.additionalInformation` },
      { linebreak: '\n' }
    ),
  }

  return (
    <>
      <AvailablePaymentMethods
        card={enableCcSurfacing && !isCreditCardBlocked}
        ach={!isACHBlocked}
        netTerms={!isNetTermsBlocked}
        wire
        renderTextMap={renderTextMap(
          () => setWireModalOpen(true),
          achSettlementMode === 'settled',
          isAchUnavailable
        )}
        collapsible
        defaultExpanded={false}
        variant={enableLdpRedesign ? 'card' : 'default'}
      >
        {isAchUnavailable ? <AchUnavailableLine /> : null}
      </AvailablePaymentMethods>
      {wireModalOpen ? (
        <>
          <WireTransferInstructionsModal
            onClose={() => setWireModalOpen(false)}
            wireAccountInfo={wireAccountInfo}
          />
        </>
      ) : null}
    </>
  )
}
